import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { PageHeader, LoadingScreen } from "../../../components/admin/EssUi";
import { useToast } from "../../../components/admin/ToastContext";
import { get, put, post, del } from "../../../services/lewooApi";
import {
  Settings, Palmtree, Clock, Banknote, BarChart3, ClipboardList, Crosshair,
  GraduationCap, FileText, Bell, Users, Link, ScrollText, ShieldCheck, KeyRound,
} from "lucide-react";

const GREEN = "#225f2b";
const BORDER = "#e2e7e2";
const SOFT = "#eef1ee";

const AdminPanelSettingsPage = () => {
  const intl = useIntl();
  const addToast = useToast();
  const [activeSection, setActiveSection] = useState("moduleAccess");

  const [admins, setAdmins] = useState([]);
  const [modules, setModules] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(null);
  const [userSearch, setUserSearch] = useState("");
  const [userResults, setUserResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [addingUid, setAddingUid] = useState(null);
  const [revokingUid, setRevokingUid] = useState(null);

  const menuItems = [
    { key: "moduleAccess", label: "Accès Modules", icon: ShieldCheck },
    { key: "managers", label: "Gestionnaires", icon: KeyRound },
    { key: "general", label: "General", icon: Settings },
    { key: "leavePolicies", label: "Leave Policies", icon: Palmtree },
    { key: "attendance", label: "Attendance Rules", icon: Clock },
    { key: "expenseLimits", label: "Expense Limits", icon: Banknote },
    { key: "payroll", label: "Payroll Settings", icon: BarChart3 },
    { key: "appraisalCycles", label: "Appraisal Cycles", icon: ClipboardList },
    { key: "recruitment", label: "Recruitment", icon: Crosshair },
    { key: "training", label: "Training", icon: GraduationCap },
    { key: "documentTemplates", label: "Document Templates", icon: FileText },
    { key: "notificationRules", label: "Notification Rules", icon: Bell },
    { key: "userRoles", label: "User Roles", icon: Users },
    { key: "integrations", label: "Integrations", icon: Link },
    { key: "auditTrail", label: "Audit Trail", icon: ScrollText },
  ];

  const loadAccess = async () => {
    setLoaded(false);
    try {
      const res = await get("/api/admin-access");
      setAdmins(res?.data || []);
      setModules(res?.modules || []);
    } catch (e) {
      addToast(e.message || "Impossible de charger les accès", "error");
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => {
    loadAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hrModules = useMemo(() => modules.filter((m) => m.group === "HR"), [modules]);
  const erpModules = useMemo(() => modules.filter((m) => m.group === "ERP"), [modules]);

  const isAdminRow = (a) => !!(a?.is_admin || a?.role === "admin" || a?.is_special);
  const adminRows = useMemo(() => admins.filter((a) => isAdminRow(a)), [admins]);
  const employeeRows = useMemo(() => admins.filter((a) => !isAdminRow(a)), [admins]);

  useEffect(() => {
    const q = userSearch.trim();
    if (!q) {
      setUserResults([]);
      setSearching(false);
      return undefined;
    }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const res = await get(`/api/users?without_admin_access=true&search=${encodeURIComponent(q)}&limit=10`);
        setUserResults(res?.data || []);
      } catch {
        setUserResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [userSearch]);

  const getCell = (admin, moduleKey) => {
    const row = admin?.modules?.[moduleKey] || {};
    return { view: !!row.view, manage: !!row.manage };
  };

  const handleToggle = async (admin, moduleKey, field) => {
    const current = getCell(admin, moduleKey);
    const next = {
      view: field === "view" ? !current.view : current.view,
      manage: field === "manage" ? !current.manage : current.manage,
    };
    if (!next.view) next.manage = false;

    const previousModules = { ...admin.modules };
    const updated = { ...admin.modules, [moduleKey]: { view: next.view, manage: next.manage } };
    setAdmins((prev) => prev.map((a) => (a.lewoo_uid === admin.lewoo_uid ? { ...a, modules: updated } : a)));

    try {
      await put(`/api/admin-access/${admin.lewoo_uid}/modules`, { modules: { [moduleKey]: { view: next.view, manage: next.manage } } });
    } catch (e) {
      addToast(e.message || "Mise à jour échouée", "error");
      setAdmins((prev) => prev.map((a) => (a.lewoo_uid === admin.lewoo_uid ? { ...a, modules: previousModules } : a)));
    }
  };

  const handleToggleSettingsAdmin = async (admin) => {
    if (toggling) return;
    setToggling(admin.lewoo_uid);
    const value = !admin.is_settings_admin;
    try {
      await put(`/api/admin-access/${admin.lewoo_uid}/settings-admin`, { is_settings_admin: value });
      setAdmins((prev) => prev.map((a) => (a.lewoo_uid === admin.lewoo_uid ? { ...a, is_settings_admin: value } : a)));
      addToast(value ? `${admin.email} est maintenant gestionnaire` : `${admin.email} n'est plus gestionnaire`, "success");
    } catch (e) {
      addToast(e.message || "Mise à jour échouée", "error");
    } finally {
      setToggling(null);
    }
  };

  const handleGrant = async (u) => {
    if (addingUid) return;
    setAddingUid(u.lewoo_uid);
    try {
      await post(`/api/admin-access/${u.lewoo_uid}`);
      await loadAccess();
      setUserSearch("");
      setUserResults([]);
      addToast(`${u.employee_name || u.login} peut maintenant accéder aux modules`, "success");
    } catch (e) {
      addToast(e.message || "Ajout échoué", "error");
    } finally {
      setAddingUid(null);
    }
  };

  const handleRevoke = async (row) => {
    if (revokingUid) return;
    const label = row.employee_name || row.login;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Retirer tout l'accès modules de ${label} ? Il perdra l'accès au panneau admin.`)) return;
    setRevokingUid(row.lewoo_uid);
    try {
      await del(`/api/admin-access/${row.lewoo_uid}`);
      setAdmins((prev) => prev.filter((a) => a.lewoo_uid !== row.lewoo_uid));
      addToast(`${label} n'a plus accès aux modules`, "success");
    } catch (e) {
      addToast(e.message || "Suppression échouée", "error");
    } finally {
      setRevokingUid(null);
    }
  };

  const ModuleMatrix = ({ admin, moduleList }) => (
    <div className="flex items-center gap-2" style={{ justifyContent: "flex-end" }}>
      <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer" title="View">
        <input
          type="checkbox"
          checked={getCell(admin, moduleList.key).view}
          disabled={saving}
          onChange={() => handleToggle(admin, moduleList.key, "view")}
          className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
        />
        V
      </label>
      <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer" title="Manage">
        <input
          type="checkbox"
          checked={getCell(admin, moduleList.key).manage}
          disabled={saving || !getCell(admin, moduleList.key).view}
          onChange={() => handleToggle(admin, moduleList.key, "manage")}
          className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
        />
        M
      </label>
    </div>
  );

  const renderModuleAccess = () => {
    if (!loaded) return <LoadingScreen />;
    if (adminRows.length === 0) {
      return (
        <div className="ess-empty" style={{ padding: "40px 20px" }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: "12px" }}><ShieldCheck size={28} color="#94a3b8" /></div>
          <p style={{ fontSize: "14px", fontWeight: "500", color: "#64748b" }}>Aucun admin à configurer</p>
          <p style={{ fontSize: "12.5px", color: "#94a3b8", marginTop: "4px" }}>Le seed crée les lignes d'accès automatiquement.</p>
        </div>
      );
    }
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        <div className="flex items-center gap-2 px-4 py-2.5 rounded-md text-sm" style={{ background: "#f0f7f0", color: GREEN, border: "1px solid #cfe3cf" }}>
          <ShieldCheck size={16} /> V = lecture · M = gestion (actions). Sans case cochée, l'admin n'a pas accès au module.
        </div>

        {[
          { title: "Modules RH", group: hrModules },
          { title: "Modules ERP", group: erpModules },
        ].map((section) => (
          <div key={section.title} style={{ background: "white", borderRadius: "8px", border: `1px solid ${BORDER}`, overflow: "hidden" }}>
            <div className="px-5 py-3 border-b" style={{ background: "#fafbfa", borderColor: BORDER }}>
              <h3 style={{ fontSize: "15px", fontWeight: "600", color: "#1e293b" }}>{section.title}</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full" style={{ borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th className="px-5 py-2.5 text-left text-xs font-bold uppercase tracking-wider text-slate-500 border-b sticky left-0" style={{ background: "#fff", minWidth: 240, borderColor: BORDER }}>Admin</th>
                    {section.group.map((m) => (
                      <th key={m.key} className="px-3 py-2.5 text-center text-xs font-bold uppercase tracking-wider text-slate-500 border-b" style={{ minWidth: 96, borderColor: BORDER }}>
                        {m.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {adminRows.map((admin) => (
                    <tr key={admin.lewoo_uid} className="hover:bg-slate-50">
                      <td className="px-5 py-2.5 border-b sticky left-0" style={{ background: "#fff", borderColor: SOFT }}>
                        <p className="text-sm font-semibold text-slate-800">{admin.employee_name || admin.login}</p>
                        <p className="text-xs text-slate-400 truncate" style={{ maxWidth: 220 }}>{admin.email}</p>
                      </td>
                      {section.group.map((m) => (
                        <td key={m.key} className="px-3 py-2.5 border-b" style={{ borderColor: SOFT }}>
                          <ModuleMatrix admin={admin} moduleList={m} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderUserRoles = () => {
    if (!loaded) return <LoadingScreen />;
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        <div style={{ background: "white", borderRadius: "8px", border: `1px solid ${BORDER}`, padding: "16px" }}>
          <h3 style={{ fontSize: "15px", fontWeight: "600", color: "#1e293b" }}>Ajouter un employé</h3>
          <p style={{ fontSize: "13px", color: "#64748b", marginTop: "2px", marginBottom: "10px" }}>
            Donnez à un employé (non-admin) l'accès à certains modules du panneau admin. Il ne devient pas administrateur.
          </p>
          <div style={{ position: "relative" }}>
            <input
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              placeholder="Rechercher par nom, email ou login..."
              className="w-full px-3 py-2 rounded-md border border-slate-200 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              style={{ borderColor: BORDER }}
            />
            {userSearch.trim() !== "" && (
              <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 20, marginTop: "4px", background: "#fff", border: `1px solid ${BORDER}`, borderRadius: "8px", overflow: "hidden", boxShadow: "0 8px 24px rgba(15,23,42,0.08)" }}>
                {searching && <p className="px-4 py-2.5 text-xs text-slate-400">Recherche...</p>}
                {!searching && userResults.length === 0 && <p className="px-4 py-2.5 text-xs text-slate-400">Aucun utilisateur trouvé.</p>}
                {!searching && userResults.map((u) => (
                  <button
                    key={u.lewoo_uid}
                    onClick={() => handleGrant(u)}
                    disabled={!!addingUid}
                    className="w-full flex items-center justify-between gap-2 px-4 py-2.5 hover:bg-slate-50 text-left disabled:opacity-60"
                  >
                    <span style={{ minWidth: 0 }}>
                      <span className="block text-sm font-semibold text-slate-800 truncate">{u.employee_name || u.login}</span>
                      <span className="block text-xs text-slate-400 truncate">{u.email}</span>
                    </span>
                    <span className="flex-shrink-0 text-xs font-semibold px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200">
                      {addingUid === u.lewoo_uid ? "Ajout..." : "+ Ajouter"}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {employeeRows.length === 0 ? (
          <div className="ess-empty" style={{ padding: "40px 20px" }}>
            <div style={{ display: "flex", justifyContent: "center", marginBottom: "12px" }}><Users size={28} color="#94a3b8" /></div>
            <p style={{ fontSize: "14px", fontWeight: "500", color: "#64748b" }}>Aucun employé autorisé</p>
            <p style={{ fontSize: "12.5px", color: "#94a3b8", marginTop: "4px" }}>Utilisez la recherche ci-dessus pour donner accès à un premier employé.</p>
          </div>
        ) : (
          [
            { title: "Modules RH", group: hrModules },
            { title: "Modules ERP", group: erpModules },
          ].map((section) => (
            <div key={section.title} style={{ background: "white", borderRadius: "8px", border: `1px solid ${BORDER}`, overflow: "hidden" }}>
              <div className="px-5 py-3 border-b flex items-center justify-between" style={{ background: "#fafbfa", borderColor: BORDER }}>
                <h3 style={{ fontSize: "15px", fontWeight: "600", color: "#1e293b" }}>{section.title}</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full" style={{ borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th className="px-5 py-2.5 text-left text-xs font-bold uppercase tracking-wider text-slate-500 border-b sticky left-0" style={{ background: "#fff", minWidth: 220, borderColor: BORDER }}>Employé</th>
                      {section.group.map((m) => (
                        <th key={m.key} className="px-3 py-2.5 text-center text-xs font-bold uppercase tracking-wider text-slate-500 border-b" style={{ minWidth: 96, borderColor: BORDER }}>
                          {m.label}
                        </th>
                      ))}
                      <th className="px-4 py-2.5 text-right text-xs font-bold uppercase tracking-wider text-slate-500 border-b" style={{ background: "#fff", minWidth: 110, borderColor: BORDER }}>Accès</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employeeRows.map((row) => (
                      <tr key={row.lewoo_uid} className="hover:bg-slate-50">
                        <td className="px-5 py-2.5 border-b sticky left-0" style={{ background: "#fff", borderColor: SOFT }}>
                          <p className="text-sm font-semibold text-slate-800">{row.employee_name || row.login}</p>
                          <p className="text-xs text-slate-400 truncate" style={{ maxWidth: 200 }}>{row.email}</p>
                        </td>
                        {section.group.map((m) => (
                          <td key={m.key} className="px-3 py-2.5 border-b" style={{ borderColor: SOFT }}>
                            <ModuleMatrix admin={row} moduleList={m} />
                          </td>
                        ))}
                        <td className="px-4 py-2.5 border-b text-right" style={{ background: "#fff", borderColor: SOFT }}>
                          <button
                            onClick={() => handleRevoke(row)}
                            disabled={revokingUid === row.lewoo_uid}
                            className="inline-flex items-center px-2.5 py-1.5 rounded-md text-xs font-semibold bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 transition-colors disabled:opacity-60"
                          >
                            {revokingUid === row.lewoo_uid ? "..." : "Retirer"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        )}
      </div>
    );
  };

  const renderManagers = () => {
    if (!loaded) return <LoadingScreen />;
    return (
      <div style={{ background: "white", borderRadius: "8px", border: `1px solid ${BORDER}`, overflow: "hidden" }}>
        <div className="px-5 py-3 border-b" style={{ background: "#fafbfa", borderColor: BORDER }}>
          <h3 style={{ fontSize: "15px", fontWeight: "600", color: "#1e293b" }}>Gestionnaires Settings</h3>
          <p style={{ fontSize: "13px", color: "#64748b", marginTop: "2px" }}>
            Un gestionnaire a accès à l'onglet Settings et aux routes d'écriture. Il n'a pas automatiquement tous les droits modules.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full" style={{ borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th className="px-5 py-2.5 text-left text-xs font-bold uppercase tracking-wider text-slate-500 border-b" style={{ borderColor: BORDER }}>Admin</th>
                <th className="px-5 py-2.5 text-left text-xs font-bold uppercase tracking-wider text-slate-500 border-b" style={{ borderColor: BORDER }}>Rôle</th>
                <th className="px-5 py-2.5 text-right text-xs font-bold uppercase tracking-wider text-slate-500 border-b" style={{ borderColor: BORDER }}>Gestionnaire</th>
              </tr>
            </thead>
            <tbody>
              {admins.map((admin) => (
                <tr key={admin.lewoo_uid} className="hover:bg-slate-50">
                  <td className="px-5 py-3 border-b" style={{ borderColor: SOFT }}>
                    <p className="text-sm font-semibold text-slate-800">{admin.employee_name || admin.login}</p>
                    <p className="text-xs text-slate-400">{admin.email}</p>
                  </td>
                  <td className="px-5 py-3 border-b text-sm text-slate-600 capitalize" style={{ borderColor: SOFT }}>{admin.role || "—"}</td>
                  <td className="px-5 py-3 border-b text-right" style={{ borderColor: SOFT }}>
                    <button
                      onClick={() => handleToggleSettingsAdmin(admin)}
                      disabled={toggling === admin.lewoo_uid}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors disabled:opacity-60 ${
                        admin.is_settings_admin
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
                          : "bg-slate-100 text-slate-500 border border-slate-200 hover:bg-slate-200"
                      }`}
                    >
                      {admin.is_settings_admin ? "✓ Actif" : "Activer"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const leavePolicies = {
    annual: {
      title: "Annual Leave",
      items: [
        { label: "Days/year", value: "18 days" },
        { label: "Carry-over max", value: "5 days" },
        { label: "Notice required", value: "7 days" },
        { label: "Half-day allowed", value: "Yes" },
      ]
    },
    sick: {
      title: "Sick Leave",
      items: [
        { label: "Days/year", value: "12 days" },
        { label: "Medical cert required", value: "After 2 days" },
        { label: "Carry-over", value: "No" },
        { label: "Advance booking", value: "No" },
      ]
    },
    compensatory: {
      title: "Compensatory",
      items: [
        { label: "How earned", value: "Approved overtime" },
        { label: "Expiry", value: "3 months" },
        { label: "Max accumulation", value: "10 days" },
        { label: "Manager approval", value: "Yes" },
      ]
    },
    maternity: {
      title: "Maternity / Paternity",
      items: [
        { label: "Maternity", value: "14 weeks" },
        { label: "Paternity", value: "10 days" },
        { label: "Paid", value: "100% (CNPS + employer)" },
        { label: "Notice required", value: "30 days" },
      ]
    }
  };

  const renderContent = () => {
    switch (activeSection) {
      case "moduleAccess":
        return renderModuleAccess();

      case "managers":
        return renderManagers();

      case "userRoles":
        return renderUserRoles();

      case "general":
        return (
          <div style={{ background: 'white', borderRadius: '8px', border: `1px solid ${BORDER}`, padding: '20px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>
              General ESS Settings
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'grid', gap: '12px', padding: '10px 0', borderBottom: `1px solid ${SOFT}` }} className="ess-rg-form-row">
                <span style={{ fontSize: '13.5px', fontWeight: '500', color: '#64748b' }}>Company Name</span>
                <span style={{ fontSize: '13.5px', color: '#1e293b' }}>Lewoo SARL / Africa Systems</span>
              </div>
              <div style={{ display: 'grid', gap: '12px', padding: '10px 0', borderBottom: `1px solid ${SOFT}` }} className="ess-rg-form-row">
                <span style={{ fontSize: '13.5px', fontWeight: '500', color: '#64748b' }}>Country</span>
                <span style={{ fontSize: '13.5px', color: '#1e293b' }}>Cameroon</span>
              </div>
              <div style={{ display: 'grid', gap: '12px', padding: '10px 0', borderBottom: `1px solid ${SOFT}` }} className="ess-rg-form-row">
                <span style={{ fontSize: '13.5px', fontWeight: '500', color: '#64748b' }}>Time Zone</span>
                <span style={{ fontSize: '13.5px', color: '#1e293b' }}>Africa/Douala (WAT — UTC+1)</span>
              </div>
              <div style={{ display: 'grid', gap: '12px', padding: '10px 0', borderBottom: `1px solid ${SOFT}` }} className="ess-rg-form-row">
                <span style={{ fontSize: '13.5px', fontWeight: '500', color: '#64748b' }}>Working Days</span>
                <span style={{ fontSize: '13.5px', color: '#1e293b' }}>Monday–Friday</span>
              </div>
              <div style={{ display: 'grid', gap: '12px', padding: '10px 0', borderBottom: `1px solid ${SOFT}` }} className="ess-rg-form-row">
                <span style={{ fontSize: '13.5px', fontWeight: '500', color: '#64748b' }}>Working Hours</span>
                <span style={{ fontSize: '13.5px', color: '#1e293b' }}>08:00–17:00 (8h/day)</span>
              </div>
              <div style={{ display: 'grid', gap: '12px', padding: '10px 0', borderBottom: `1px solid ${SOFT}` }} className="ess-rg-form-row">
                <span style={{ fontSize: '13.5px', fontWeight: '500', color: '#64748b' }}>Self-Service Portal URL</span>
                <span style={{ fontSize: '13.5px', color: GREEN }}>https://ess.lewoo.com</span>
              </div>
              <div style={{ display: 'grid', gap: '12px', padding: '10px 0', borderBottom: `1px solid ${SOFT}` }} className="ess-rg-form-row">
                <span style={{ fontSize: '13.5px', fontWeight: '500', color: '#64748b' }}>Allow Remote Work Requests</span>
                <span style={{ fontSize: '13.5px', color: '#1a7a37', fontWeight: '600' }}>Yes</span>
              </div>
              <div style={{ display: 'grid', gap: '12px', padding: '10px 0' }} className="ess-rg-form-row">
                <span style={{ fontSize: '13.5px', fontWeight: '500', color: '#64748b' }}>Require Manager Approval</span>
                <span style={{ fontSize: '13.5px', color: GREEN, fontWeight: '600' }}>✓</span>
              </div>
            </div>
          </div>
        );

      case "leavePolicies":
        return (
          <div style={{ display: 'grid', gap: '14px' }} className="ess-rg-2">
            {Object.values(leavePolicies).map((policy) => (
              <div key={policy.title} style={{ background: 'white', borderRadius: '8px', border: `1px solid ${BORDER}`, padding: '18px' }}>
                <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b', marginBottom: '12px', paddingBottom: '10px', borderBottom: `2px solid ${GREEN}` }}>
                  {policy.title}
                </h4>
                {policy.items.map((item) => (
                  <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: `1px solid ${SOFT}` }}>
                    <span style={{ fontSize: '13px', color: '#64748b' }}>{item.label}</span>
                    <span style={{ fontSize: '13px', fontWeight: '500', color: '#1e293b' }}>{item.value}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        );

      default:
        return (
          <div className="ess-empty" style={{ padding: "40px 20px" }}>
            <div style={{ display: "flex", justifyContent: "center", marginBottom: "12px" }}><Settings size={28} color="#94a3b8" /></div>
            <p style={{ fontSize: '14px', fontWeight: '500', color: '#64748b' }}>Settings for {activeSection.replace(/([A-Z])/g, ' $1').trim()}</p>
            <p style={{ fontSize: '12.5px', color: '#94a3b8', marginTop: '4px' }}>Configuration options will appear here</p>
          </div>
        );
    }
  };

  return (
    <div className="max-w-7xl mx-auto animate-fade-in" style={{ padding: '0 16px' }}>
      <PageHeader
        title="ESS Settings"
        subtitle="Configure employee self-service rules and policies"
      />

      <div style={{ display: 'grid', gap: '20px' }} className="ess-rg-side">
        <div style={{ background: 'white', borderRadius: '8px', border: `1px solid ${BORDER}`, padding: '6px', height: 'fit-content' }}>
          {menuItems.map((item) => (
            <button
              key={item.key}
              onClick={() => setActiveSection(item.key)}
              style={{
                width: '100%',
                padding: '9px 14px',
                border: 'none',
                background: activeSection === item.key ? '#f0f7f0' : 'transparent',
                color: activeSection === item.key ? GREEN : '#475569',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: activeSection === item.key ? '600' : '500',
                cursor: 'pointer',
                transition: 'all 0.15s',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                textAlign: 'left'
              }}
              onMouseEnter={(e) => {
                if (activeSection !== item.key) {
                  e.target.style.background = '#f8fafc';
                }
              }}
              onMouseLeave={(e) => {
                if (activeSection !== item.key) {
                  e.target.style.background = 'transparent';
                }
              }}
            >
              <item.icon size={16} className="flex-shrink-0" />
              <span>{item.label}</span>
              {activeSection === item.key && (
                <span style={{ marginLeft: 'auto', color: GREEN }}>▸</span>
              )}
            </button>
          ))}
        </div>

        <div>
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default AdminPanelSettingsPage;